import React, { useEffect, useMemo, useState } from "react";
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDocs,
  onSnapshot,
  serverTimestamp,
  Timestamp,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";
import {
  CalendarDays,
  CalendarRange,
  PartyPopper,
  Plus,
  Search,
  Trash2,
  X,
} from "lucide-react";
import { db } from "../firebase";
import MenuBar from "../components/MenuBar";
import HeaderBar from "../components/HeaderBar";
import { ATTENDANCE_BRANCHES } from "../config/attendanceBranches";
import {
  employeesBlockedFromPaidLeave,
  MONTHLY_PAID_LEAVE_ALLOWANCE,
} from "../config/employeeLeavePolicy";
import { resolveCollectionName } from "../services/attendanceCollections";

function ymd(d) {
  const x = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const y = x.getFullYear();
  const m = String(x.getMonth() + 1).padStart(2, "0");
  const day = String(x.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/** Prefer dateTs, else dateStr / date (yyyy-MM-dd). */
function parseDocDate(data) {
  if (!data || typeof data !== "object") return null;
  const ts = data.dateTs;
  if (ts != null && typeof ts.toDate === "function") {
    return ts.toDate();
  }
  const s = (data.dateStr ?? data.date)?.toString()?.trim();
  if (s) {
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
    if (m) {
      return new Date(+m[1], +m[2] - 1, +m[3]);
    }
    const t = new Date(s);
    return isNaN(t.getTime()) ? null : t;
  }
  return null;
}

function startOfToday() {
  const t = new Date();
  return new Date(t.getFullYear(), t.getMonth(), t.getDate());
}

export default function HolidaysPage() {
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [selectedDate, setSelectedDate] = useState(null);
  const [entryType, setEntryType] = useState("company_calendar");
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState([]);
  const [employeePickerOpen, setEmployeePickerOpen] = useState(false);
  const [employeePickerLoading, setEmployeePickerLoading] = useState(false);
  const [employeePickerSearch, setEmployeePickerSearch] = useState("");
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");
  const [filterMode, setFilterMode] = useState("all");
  const [holidayModalOpen, setHolidayModalOpen] = useState(false);

  const resetHolidayForm = () => {
    setName("");
    setDescription("");
    setSelectedDate(null);
    setEntryType("company_calendar");
    setSelectedEmployeeIds([]);
    setEmployeePickerOpen(false);
    setEmployeePickerSearch("");
  };

  const openHolidayModal = () => {
    resetHolidayForm();
    setHolidayModalOpen(true);
  };

  const closeHolidayModal = () => {
    setHolidayModalOpen(false);
    resetHolidayForm();
  };

  const loadEmployeeOptions = async () => {
    if (employeeOptions.length > 0) return;
    setEmployeePickerLoading(true);
    try {
      const merged = new Map();
      for (const branch of ATTENDANCE_BRANCHES) {
        const employeeCollection = await resolveCollectionName(branch.employeeCandidates || []);
        if (!employeeCollection) continue;
        const snap = await getDocs(collection(db, employeeCollection));
        for (const docSnap of snap.docs) {
          const d = docSnap.data() || {};
          const employeeId = String(
            d.employeeId ?? d.empId ?? d.user_id ?? d.userId ?? d.EmployeeId ?? docSnap.id ?? ""
          ).trim();
          if (!employeeId) continue;
          const employeeName = String(
            d.EmployeeName ?? d.employeeName ?? d.fullName ?? d.name ?? d.Name ?? employeeId
          ).trim();
          const key = employeeId.toLowerCase();
          if (!merged.has(key)) {
            merged.set(key, { employeeId, employeeName, branchLabel: branch.label });
          }
        }
      }
      setEmployeeOptions(Array.from(merged.values()).sort((a, b) => a.employeeName.localeCompare(b.employeeName)));
    } catch (err) {
      console.error(err);
      toast.error("Could not load employees");
    } finally {
      setEmployeePickerLoading(false);
    }
  };

  const openEmployeePicker = async () => {
    setEmployeePickerOpen(true);
    await loadEmployeeOptions();
  };

  useEffect(() => {
    const col = collection(db, "holidays");
    const unsub = onSnapshot(
      col,
      (snap) => {
        const list = [...snap.docs].sort((a, b) => {
          const da = parseDocDate(a.data());
          const db = parseDocDate(b.data());
          if (da == null && db == null) return 0;
          if (da == null) return 1;
          if (db == null) return -1;
          return db.getTime() - da.getTime();
        });
        setDocs(list);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err?.message || String(err));
        setLoading(false);
      }
    );
    return () => unsub();
  }, []);

  const dateInputValue = useMemo(() => {
    if (!selectedDate) return "";
    return ymd(selectedDate);
  }, [selectedDate]);

  const holidayRows = useMemo(() => {
    return docs.map((d) => {
      const data = d.data() || {};
      const dt = parseDocDate(data);
      return { id: d.id, data, dt };
    });
  }, [docs]);

  const stats = useMemo(() => {
    const todayStart = startOfToday();
    let upcoming = 0;
    let past = 0;
    for (const r of holidayRows) {
      if (!r.dt) continue;
      const day = new Date(r.dt.getFullYear(), r.dt.getMonth(), r.dt.getDate());
      if (day >= todayStart) upcoming += 1;
      else past += 1;
    }
    return { total: holidayRows.length, upcoming, past };
  }, [holidayRows]);

  const filteredRows = useMemo(() => {
    const todayStart = startOfToday();
    const q = search.trim().toLowerCase();
    let list = holidayRows;
    if (q) {
      list = list.filter((r) => {
        const n = (r.data.name || "").toString().toLowerCase();
        const desc = (r.data.description || "").toString().toLowerCase();
        return n.includes(q) || desc.includes(q);
      });
    }
    if (filterMode === "upcoming") {
      list = list.filter((r) => r.dt && new Date(r.dt.getFullYear(), r.dt.getMonth(), r.dt.getDate()) >= todayStart);
    } else if (filterMode === "past") {
      list = list.filter((r) => r.dt && new Date(r.dt.getFullYear(), r.dt.getMonth(), r.dt.getDate()) < todayStart);
    }
    return list;
  }, [holidayRows, search, filterMode]);

  const filteredEmployeeOptions = useMemo(() => {
    const q = employeePickerSearch.trim().toLowerCase();
    if (!q) return employeeOptions;
    return employeeOptions.filter((emp) => {
      return emp.employeeName.toLowerCase().includes(q) || emp.employeeId.toLowerCase().includes(q);
    });
  }, [employeeOptions, employeePickerSearch]);

  const addHoliday = async (e) => {
    e.preventDefault();
    const n = name.trim();
    if (!n) {
      toast.error("Please enter holiday name");
      return;
    }
    if (!selectedDate) {
      toast.error("Please pick a date");
      return;
    }
    const dateOnly = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), selectedDate.getDate());
    const dateStr = ymd(dateOnly);
    const isEmployeeCashLeave = entryType === "employee_cash_leave";
    const employeeIds = selectedEmployeeIds;
    if (isEmployeeCashLeave && employeeIds.length === 0) {
      toast.error("Select at least one employee");
      return;
    }
    if (isEmployeeCashLeave) {
      const blocked = employeesBlockedFromPaidLeave(docs, employeeIds, dateOnly);
      if (blocked.length) {
        const names = blocked.map((b) => {
          const opt = employeeOptions.find(
            (e) => e.employeeId.toLowerCase() === b.employeeId.toLowerCase()
          );
          return opt ? `${opt.employeeName} (${b.employeeId})` : b.employeeId;
        });
        toast.error(
          `Monthly limit: ${MONTHLY_PAID_LEAVE_ALLOWANCE} paid leave days per employee. Already at limit: ${names.join(", ")}`
        );
        return;
      }
    }
    try {
      await addDoc(collection(db, "holidays"), {
        name: n,
        description: description.trim(),
        date: dateStr,
        dateStr,
        dateTs: Timestamp.fromDate(dateOnly),
        createdAt: serverTimestamp(),
        /** Company holiday or employee-specific cash leave entry. */
        recordType: isEmployeeCashLeave ? "employee_cash_leave" : "company_calendar",
        countsAsEmployeeLeave: isEmployeeCashLeave,
        employeeIds: isEmployeeCashLeave ? Array.from(new Set(employeeIds)) : [],
      });
      closeHolidayModal();
      toast.success(isEmployeeCashLeave ? "Cash leave added" : "Holiday added");
    } catch (err) {
      console.error(err);
      toast.error(err?.message || String(err));
    }
  };

  const deleteHoliday = async (id) => {
    if (!window.confirm("Delete this holiday?")) return;
    try {
      await deleteDoc(doc(db, "holidays", id));
      toast.success("Holiday deleted");
    } catch (err) {
      toast.error(err?.message || String(err));
    }
  };

  const todayStartForUi = startOfToday();

  return (
    <div className="min-h-screen bg-slate-100/90">
      <Toaster position="top-right" />
      <MenuBar />
      <HeaderBar />
      <main className="max-w-3xl mx-auto px-3 sm:px-4 pb-16 pt-2 sm:pt-4">
        <header className="mb-4 flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div className="min-w-0 flex-1">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900">Holidays</h1>
            <p className="text-sm text-slate-600 mt-1 max-w-xl leading-snug">
              Company-wide calendar dates and optional paid days for selected staff. Stored in Firestore{" "}
              <code className="text-xs bg-slate-200/80 px-1 rounded">holidays</code> (also drives working-day counts on
              payroll).
            </p>
            <div
              className="mt-3 rounded-xl border border-amber-200/90 bg-amber-50/90 px-3 py-2.5 text-xs text-amber-950 leading-snug max-w-xl"
              role="note"
            >
              <strong className="font-semibold">Default behavior:</strong> Company holidays are global dates only. For
              staff paid leave, choose <strong className="font-semibold">Employee cash leave</strong> and select
              employees — <strong className="font-semibold">{MONTHLY_PAID_LEAVE_ALLOWANCE} days per employee per month</strong>.
            </div>
          </div>
          <button
            type="button"
            onClick={openHolidayModal}
            className="inline-flex items-center justify-center gap-2 shrink-0 px-4 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 shadow-md transition-colors w-full sm:w-auto"
          >
            <Plus className="w-4 h-4" aria-hidden />
            Set holiday
          </button>
        </header>

        <div className="rounded-2xl border border-slate-200/80 bg-white shadow-sm overflow-hidden mb-4">
          <div className="grid grid-cols-3 gap-2 p-3 sm:p-4 bg-slate-50 border-b border-slate-100">
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm text-center">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Total</div>
              <div className="text-xl font-bold tabular-nums text-slate-900">{stats.total}</div>
            </div>
            <div className="rounded-xl border border-emerald-200/80 bg-emerald-50/50 px-3 py-2.5 shadow-sm text-center">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/90">Upcoming</div>
              <div className="text-xl font-bold tabular-nums text-emerald-900">{stats.upcoming}</div>
            </div>
            <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5 shadow-sm text-center">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-500">Past</div>
              <div className="text-xl font-bold tabular-nums text-slate-700">{stats.past}</div>
            </div>
          </div>

          <div className="p-3 sm:p-4 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" aria-hidden />
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search name or description…"
                  className="w-full border border-slate-200 rounded-xl pl-9 pr-9 py-2 text-sm bg-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-300 outline-none"
                  aria-label="Search holidays"
                />
                {search.trim() ? (
                  <button
                    type="button"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 rounded-lg text-slate-400 hover:bg-slate-100 hover:text-slate-700"
                    onClick={() => setSearch("")}
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                ) : null}
              </div>
              <div className="inline-flex rounded-xl border border-slate-200 p-0.5 bg-slate-50 shrink-0">
                {[
                  { id: "all", label: "All" },
                  { id: "upcoming", label: "Upcoming" },
                  { id: "past", label: "Past" },
                ].map((opt) => (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setFilterMode(opt.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${
                      filterMode === opt.id ? "bg-white shadow text-rose-800" : "text-slate-600 hover:text-slate-900"
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>

            <h2 className="text-sm font-bold text-slate-900 flex items-center gap-2">
              <CalendarRange className="w-4 h-4 text-slate-500" aria-hidden />
              All holidays
              <span className="text-slate-400 font-normal text-xs">({filteredRows.length} shown)</span>
            </h2>

            {error && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800" role="alert">
                <strong className="font-semibold">Could not load holidays.</strong> {error}
              </div>
            )}

            {!error && loading && (
              <div className="space-y-2">
                {[1, 2, 3, 4, 5].map((i) => (
                  <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
                ))}
              </div>
            )}

            {!error && !loading && filteredRows.length === 0 && (
              <div className="text-center py-12 px-4 rounded-2xl border border-dashed border-slate-200 bg-slate-50/50">
                <CalendarDays className="w-10 h-10 text-slate-300 mx-auto mb-2" aria-hidden />
                <p className="font-semibold text-slate-800 text-sm">
                  {holidayRows.length === 0 ? "No holidays yet" : "No holidays match"}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {holidayRows.length === 0 ? (
                    <>
                      Use <strong className="text-slate-700">Set holiday</strong> to add your first entry.
                    </>
                  ) : (
                    "Try another filter or clear search."
                  )}
                </p>
                {holidayRows.length === 0 ? (
                  <button
                    type="button"
                    onClick={openHolidayModal}
                    className="mt-3 inline-flex items-center justify-center gap-2 px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700"
                  >
                    <Plus className="w-4 h-4" aria-hidden />
                    Set holiday
                  </button>
                ) : null}
              </div>
            )}

            {!error && !loading && filteredRows.length > 0 && (
              <ul className="space-y-2">
                {filteredRows.map((row) => {
                  const data = row.data;
                  const dt = row.dt;
                  const dateText = dt != null ? ymd(dt) : (data.date?.toString() ?? "—");
                  const dayStart = dt ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()) : null;
                  const isUpcoming = dayStart && dayStart >= todayStartForUi;
                  const weekday =
                    dt != null
                      ? dt.toLocaleDateString("en-IN", { weekday: "short" })
                      : null;
                  return (
                    <li
                      key={row.id}
                      className="group rounded-xl border border-slate-200 bg-white p-3 sm:p-4 flex gap-3 shadow-sm hover:border-rose-200/80 hover:shadow transition-shadow"
                    >
                      <div className="flex flex-col items-center justify-center w-14 shrink-0 rounded-lg bg-rose-50 border border-rose-100 text-center py-1.5">
                        {dt ? (
                          <>
                            <span className="text-[10px] font-bold uppercase text-rose-700 leading-none">
                              {dt.toLocaleDateString("en-IN", { month: "short" })}
                            </span>
                            <span className="text-lg font-black text-rose-900 tabular-nums leading-tight">
                              {String(dt.getDate()).padStart(2, "0")}
                            </span>
                            <span className="text-[9px] text-rose-600/90">{dt.getFullYear()}</span>
                          </>
                        ) : (
                          <span className="text-[10px] text-rose-700 font-semibold px-1">No date</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <h3 className="font-semibold text-slate-900 text-sm sm:text-base">
                            {data.name?.toString() ?? "Unnamed"}
                          </h3>
                          {dt && (
                            <span
                              className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${
                                isUpcoming
                                  ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                                  : "bg-slate-100 text-slate-600 border border-slate-200"
                              }`}
                            >
                              {isUpcoming ? "Upcoming" : "Past"}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-600 mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0">
                          <span className="font-mono tabular-nums">{dateText}</span>
                          {weekday && <span className="text-slate-500">· {weekday}</span>}
                          {data.recordType === "employee_cash_leave" ? (
                            <span className="inline-flex items-center rounded-full border border-indigo-200 bg-indigo-50 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-indigo-800">
                              Employee cash leave
                            </span>
                          ) : null}
                        </div>
                        {data.recordType === "employee_cash_leave" && Array.isArray(data.employeeIds) && data.employeeIds.length > 0 ? (
                          <p className="mt-1 text-[11px] text-indigo-700">
                            Employees: <span className="font-mono">{data.employeeIds.join(", ")}</span>
                          </p>
                        ) : null}
                        {(data.description || "").toString().trim() ? (
                          <p className="text-xs text-slate-600 mt-2 leading-relaxed whitespace-pre-wrap border-t border-slate-100 pt-2">
                            {String(data.description)}
                          </p>
                        ) : null}
                      </div>
                      <button
                        type="button"
                        onClick={() => deleteHoliday(row.id)}
                        className="shrink-0 self-start p-2 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        title="Delete holiday"
                        aria-label="Delete holiday"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      </main>

      {holidayModalOpen && (
        <div
          className="fixed inset-0 z-[1700] flex items-center justify-center p-4 bg-black/50"
          role="presentation"
          onClick={closeHolidayModal}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-md w-full max-h-[min(90vh,560px)] overflow-y-auto border border-slate-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="holiday-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-gradient-to-br from-rose-600 to-rose-800 text-white px-4 py-4 sm:px-5 rounded-t-2xl flex items-start justify-between gap-3">
              <div className="flex items-start gap-3 min-w-0">
                <div className="rounded-xl bg-white/15 p-2 shrink-0">
                  <PartyPopper className="w-6 h-6 text-rose-100" aria-hidden />
                </div>
                <div>
                  <h2 id="holiday-modal-title" className="text-base font-bold">
                    Set holiday
                  </h2>
                  <p className="text-xs text-rose-100/85 mt-0.5">Enter name, optional note, and date.</p>
                </div>
              </div>
              <button
                type="button"
                onClick={closeHolidayModal}
                className="shrink-0 p-2 rounded-lg text-white/90 hover:bg-white/15"
                aria-label="Close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={addHoliday} className="p-4 sm:p-5 space-y-3">
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Entry type</span>
                <select
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white shadow-inner focus:ring-2 focus:ring-rose-500/25 focus:border-rose-400 outline-none"
                  value={entryType}
                  onChange={(e) => setEntryType(e.target.value)}
                >
                  <option value="company_calendar">Company holiday (all staff)</option>
                  <option value="employee_cash_leave">Employee cash leave (selected staff)</option>
                </select>
                <p className="mt-1.5 text-[11px] text-slate-500 leading-snug">
                  Company holidays are <strong className="text-slate-700">global dates only</strong>. Employee cash leave
                  is limited to <strong className="text-slate-700">{MONTHLY_PAID_LEAVE_ALLOWANCE} days per employee per calendar month</strong>.
                </p>
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Holiday name</span>
                <input
                  required
                  autoFocus
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white shadow-inner focus:ring-2 focus:ring-rose-500/25 focus:border-rose-400 outline-none"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g. Diwali, Republic Day"
                />
              </label>
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Description (optional)</span>
                <textarea
                  className="mt-1 w-full border border-slate-200 rounded-xl px-3 py-2.5 text-sm min-h-[80px] bg-white shadow-inner focus:ring-2 focus:ring-rose-500/25 focus:border-rose-400 outline-none resize-y"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  placeholder="Branch closure, half day, etc."
                />
              </label>
              {entryType === "employee_cash_leave" && (
                <div className="space-y-2 rounded-xl border border-indigo-100 bg-indigo-50/40 p-3">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-slate-700">Employees</span>
                    <button
                      type="button"
                      onClick={openEmployeePicker}
                      className="inline-flex items-center rounded-lg border border-indigo-200 bg-white px-2.5 py-1.5 text-xs font-semibold text-indigo-800 hover:bg-indigo-50"
                    >
                      Open employee list
                    </button>
                  </div>
                  {selectedEmployeeIds.length > 0 ? (
                    <p className="text-xs text-indigo-800">
                      {selectedEmployeeIds.length} selected:{" "}
                      <span className="font-mono">{selectedEmployeeIds.join(", ")}</span>
                    </p>
                  ) : (
                    <p className="text-xs text-indigo-700">
                      No employee selected. Max {MONTHLY_PAID_LEAVE_ALLOWANCE} paid leave days per employee per month.
                    </p>
                  )}
                </div>
              )}
              <label className="block">
                <span className="text-xs font-semibold text-slate-700">Date</span>
                <input
                  type="date"
                  className="mt-1 w-full max-w-[220px] border border-slate-200 rounded-xl px-3 py-2.5 text-sm bg-white focus:ring-2 focus:ring-rose-500/25 focus:border-rose-400 outline-none"
                  value={dateInputValue}
                  min="2020-01-01"
                  max="2100-12-31"
                  onChange={(e) => {
                    const v = e.target.value;
                    if (!v) {
                      setSelectedDate(null);
                      return;
                    }
                    const [y, m, d] = v.split("-").map(Number);
                    setSelectedDate(new Date(y, m - 1, d));
                  }}
                />
              </label>
              <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-2 pt-2 border-t border-slate-100">
                <button
                  type="button"
                  onClick={closeHolidayModal}
                  className="w-full sm:w-auto px-4 py-2.5 rounded-xl border border-slate-300 text-sm font-semibold text-slate-800 hover:bg-slate-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="w-full sm:w-auto inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-rose-600 text-white text-sm font-semibold hover:bg-rose-700 shadow-sm"
                >
                  <Plus className="w-4 h-4" aria-hidden />
                  Save holiday
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      {holidayModalOpen && employeePickerOpen && (
        <div
          className="fixed inset-0 z-[1800] flex items-center justify-center p-4 bg-black/55"
          role="presentation"
          onClick={() => setEmployeePickerOpen(false)}
        >
          <div
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[min(88vh,700px)] overflow-hidden border border-slate-200"
            role="dialog"
            aria-modal="true"
            aria-labelledby="employee-picker-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex items-center justify-between gap-3">
              <h3 id="employee-picker-title" className="text-sm font-bold text-slate-900">
                Select employees
              </h3>
              <button
                type="button"
                onClick={() => setEmployeePickerOpen(false)}
                className="rounded-lg p-1.5 text-slate-500 hover:bg-slate-200/70 hover:text-slate-800"
                aria-label="Close employee picker"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="p-3 border-b border-slate-100">
              <input
                type="search"
                value={employeePickerSearch}
                onChange={(e) => setEmployeePickerSearch(e.target.value)}
                placeholder="Search employee name or ID..."
                className="w-full border border-slate-200 rounded-xl px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-indigo-500/20 focus:border-indigo-300 outline-none"
              />
            </div>
            <div className="p-3 overflow-y-auto max-h-[52vh]">
              {employeePickerLoading ? (
                <p className="text-sm text-slate-500">Loading employees...</p>
              ) : filteredEmployeeOptions.length === 0 ? (
                <p className="text-sm text-slate-500">No employees found.</p>
              ) : (
                <ul className="space-y-1.5">
                  {filteredEmployeeOptions.map((emp) => {
                    const checked = selectedEmployeeIds.includes(emp.employeeId);
                    return (
                      <li key={emp.employeeId}>
                        <label className="flex items-start gap-2.5 rounded-lg border border-slate-200 bg-white px-2.5 py-2 hover:bg-slate-50 cursor-pointer">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            checked={checked}
                            onChange={(e) => {
                              const isChecked = e.target.checked;
                              setSelectedEmployeeIds((prev) => {
                                if (isChecked) return Array.from(new Set([...prev, emp.employeeId]));
                                return prev.filter((id) => id !== emp.employeeId);
                              });
                            }}
                          />
                          <span className="min-w-0">
                            <span className="block text-sm font-semibold text-slate-900">{emp.employeeName}</span>
                            <span className="block text-[11px] text-slate-500 font-mono">
                              {emp.employeeId} · {emp.branchLabel}
                            </span>
                          </span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div className="px-3 py-2.5 border-t border-slate-200 bg-white flex items-center justify-between gap-2">
              <p className="text-xs text-slate-600">{selectedEmployeeIds.length} selected</p>
              <button
                type="button"
                onClick={() => setEmployeePickerOpen(false)}
                className="inline-flex items-center rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700"
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
